import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, FlaskConical } from "lucide-react";
import MaskedText from "@/components/motion/MaskedText";
import { RESEARCH_NOTICE } from "@/lib/store";

const VIDEOS = [
  { src: "/assets/videos/web_hero1.mp4", poster: "/assets/videos/poster1.jpg" },
  { src: "/assets/videos/web_hero2.mp4", poster: "/assets/videos/poster2.jpg" },
  { src: "/assets/videos/web_hero3.mp4", poster: "/assets/videos/poster3.jpg" },
];

export default function Hero() {
  const [active, setActive] = useState(0);
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const mediaY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 90]);

  useEffect(() => {
    if (reduce) return;
    const id = setInterval(() => setActive((a) => (a + 1) % VIDEOS.length), 8000);
    return () => clearInterval(id);
  }, [reduce]);

  return (
    <section ref={ref} className="relative bg-[#111C32] overflow-hidden" data-testid="hero">
      {/* Media layer */}
      <div className="absolute inset-0">
        <motion.div className="absolute inset-0" style={reduce ? undefined : { y: mediaY }}>
          {VIDEOS.map((v, i) => (
            <div
              key={i}
              className="absolute inset-0 transition-opacity duration-[1400ms] ease-in-out"
              style={{ opacity: i === active ? 1 : 0 }}
            >
              {reduce ? (
                <img src={v.poster} alt="" aria-hidden="true" className="w-full h-full object-cover" />
              ) : (
                <video
                  className="w-full h-full object-cover"
                  src={v.src}
                  poster={v.poster}
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload={i === 0 ? "auto" : "none"}
                  aria-hidden="true"
                />
              )}
            </div>
          ))}
        </motion.div>
        {/* Legibility overlays (solid, brand-tinted) */}
        <div className="absolute inset-0 bg-[#111C32]/80" />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(17,28,50,0.96)_0%,rgba(17,28,50,0.7)_45%,rgba(17,28,50,0.35)_100%)]" />
      </div>

      {/* Content */}
      <motion.div
        className="container-rnv relative z-10 min-h-[calc(100vh-125px)] flex flex-col justify-center py-24"
        style={reduce ? undefined : { y: textY }}
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          className="flex items-center gap-3 mb-7"
        >
          <span className="h-px w-10 bg-[#C8A96A]" />
          <span className="overline !text-[#D8BE87]">Renova Peptide Co.</span>
        </motion.div>

        <h1 className="font-serif font-light text-[#F7F4EF] text-5xl md:text-7xl lg:text-[5.4rem] leading-[1.02] tracking-tight max-w-4xl">
          <MaskedText lines={["Precision for", "Modern Research"]} />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.8 }}
          className="mt-8 text-lg md:text-xl text-[#E7E9EE] max-w-xl leading-relaxed"
          style={{ textShadow: "0 1px 12px rgba(17,28,50,0.6)" }}
        >
          Renova Peptide Co. provides carefully presented research compounds for
          qualified laboratory and analytical applications.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.8 }}
          className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4"
        >
          <Link to="/shop" className="btn-gold-solid" style={{ backgroundColor: "#C8A96A", color: "#111C32" }} data-testid="hero-shop-btn">
            Shop Research Products <ArrowRight size={17} />
          </Link>
          <Link to="/quality-standards" className="btn-gold !text-[#F7F4EF] !border-[#C8A96A] hover:!text-[#111C32] backdrop-blur-sm" style={{ backgroundColor: "rgba(17,28,50,0.4)" }} data-testid="hero-standards-btn">
            Explore Quality Standards
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-12 inline-flex items-center gap-2.5 text-xs text-[#D6D9E0] border border-[#C8A96A]/40 rounded-sm px-4 py-2.5 w-fit bg-[#111C32]/40 backdrop-blur-sm"
          data-testid="hero-research-notice"
        >
          <FlaskConical size={15} className="text-[#C8A96A]" />
          {RESEARCH_NOTICE}
        </motion.div>
      </motion.div>

      {/* Slide indicators */}
      {!reduce && (
        <div className="absolute bottom-6 right-6 z-10 flex gap-2">
          {VIDEOS.map((_, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              aria-label={`Show background ${i + 1}`}
              className={`h-1 rounded-full transition-all duration-500 ${i === active ? "w-8 bg-[#C8A96A]" : "w-3 bg-white/30"}`}
              data-testid={`hero-video-dot-${i}`}
            />
          ))}
        </div>
      )}
    </section>
  );
}
